import { useEffect, useMemo, useState, useRef, createElement } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import { MapContainer, TileLayer, WMSTileLayer, Marker, Popup, Tooltip, Polyline, Polygon, CircleMarker, useMapEvents, useMap } from "react-leaflet";
import L from "leaflet";
const _iconSvgCache = {};
import { CABLE_DEF, ductColor, microductColor } from "@/lib/lineColors";
export const escHtml = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
function moduleIconSvg(moduleKey, color) {
  const k = moduleKey + "|" + color;
  if (_iconSvgCache[k] !== undefined) return _iconSvgCache[k];
  let svg = "";
  try { const Ico = MODULES[moduleKey] && MODULES[moduleKey].icon; if (Ico) svg = renderToStaticMarkup(createElement(Ico, { width: 14, height: 14, color })); } catch { svg = ""; }
  _iconSvgCache[k] = svg;
  return svg;
}
import "leaflet.markercluster";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import { MARKER_SHAPES } from "@/lib/markerShapes";
import { Plus, Minus } from "lucide-react";
import { MODULES } from "@/lib/modules";
import { CLIENT_TYPES, clientType, clientTypeColor } from "@/lib/clientTypes";
import { useI18n } from "@/context/I18nContext";
import { OSM_TILE_URL, OSM_ATTR, tileSubdomains } from "@/lib/mapTiles";
import api from "@/lib/api";
import WifiMapLayer from "@/components/wifi/WifiMapLayer";

// Guard against a react-leaflet + markercluster race: during a flyTo zoom
// transition a layer remount can leave a pane without _leaflet_pos, which
// otherwise throws "Cannot read properties of undefined (reading _leaflet_pos)".
const _origGetPosition = L.DomUtil.getPosition;
L.DomUtil.getPosition = function (el) {
  if (!el || !el._leaflet_pos) return new L.Point(0, 0);
  return _origGetPosition(el);
};

// Cluster large point datasets (e.g. thousands of poles) for smooth rendering.
function PointCluster({ items, color, moduleKey, singular, display, onSelect, drawMode, onDrawClick, addMode, onFeaturePick, onAddSlack, iconSpec, scale = 1 }) {
  const map = useMap();
  const handlers = useRef({});
  handlers.current = { onSelect, onDrawClick, onFeaturePick, onAddSlack, drawMode, addMode };
  // Rebuild the cluster only when the underlying set actually changes, not on
  // every parent re-render / data refetch (avoids remount races during flyTo).
  const sig = useMemo(() => {
    const a = items || [];
    return `${a.length}:${a[0]?.id || ""}:${a[a.length - 1]?.id || ""}:${a.map((it) => it.color || "").join(",")}`;
  }, [items]);
  useEffect(() => {
    const pts = (items || []).filter((it) => it.lat != null && it.lng != null);
    if (!pts.length) return;
    const group = L.markerClusterGroup({
      maxClusterRadius: 55,
      disableClusteringAtZoom: 17,
      spiderfyOnMaxZoom: true,
      iconCreateFunction: (cluster) => {
        const count = cluster.getChildCount();
        // Bąbelek klastra skaluje się wraz z zoomem (scale zawiera zoomFactor) —
        // przy oddalaniu mapy klastry robią się małe.
        const sz = Math.max(11, Math.round(28 * scale));
        const fs = Math.max(8, Math.round(12 * scale));
        const bg = count < 10 ? "#22C55E" : count < 100 ? "#F59E0B" : "#EF4444";
        return L.divIcon({
          className: "fp-cluster",
          iconSize: [sz, sz],
          html: `<div style="width:${sz}px;height:${sz}px;line-height:${sz}px;border-radius:50%;background:${bg};color:#0b1120;font-weight:700;font-size:${fs}px;text-align:center;border:2px solid rgba(255,255,255,0.85);box-shadow:0 1px 4px rgba(0,0,0,0.4)">${count}</div>`,
        });
      },
    });
    pts.forEach((it) => {
      const m = L.marker([it.lat, it.lng], {
        icon: L.divIcon({ className: "", html: iconInner(it.color || color, iconSpec, "", scale), iconSize: [26, 26], iconAnchor: [13, 13] }),
      });
      const label = escHtml(it[display] || it.name || "—");
      const sing = escHtml(singular);
      const pre = iconSpec && iconSpec.img ? `<img src="${escHtml(iconSpec.img)}" style="width:14px;height:14px;object-fit:contain;vertical-align:middle;margin-right:4px" />` : (iconSpec && iconSpec.glyph ? `<span style="margin-right:4px">${escHtml(iconSpec.glyph)}</span>` : "");
      m.bindPopup(`${pre}<span style="display:inline-block;vertical-align:middle;margin-right:5px">${moduleIconSvg(moduleKey, color)}</span><b>${sing}</b>: ${label}<div style="margin-top:6px;display:flex;flex-direction:column;gap:4px"><button class="fp-add-slack" style="padding:4px 8px;background:#F43F5E;color:#fff;border:0;border-radius:4px;cursor:pointer;font-size:12px">+ Dodaj zapas kabla</button><button class="fp-open-card" style="padding:4px 8px;background:#0891b2;color:#fff;border:0;border-radius:4px;cursor:pointer;font-size:12px">Wyświetl kartę obiektu</button></div>`);
      // W trybie rysowania kabla NIE pokazuj etykiet (zasłaniają słupy i utrudniają klikanie).
      if (!drawMode) m.bindTooltip(`${sing}: ${label}`, { direction: "top", offset: [0, -10], opacity: 0.95 });
      m.on("click", () => {
        const h = handlers.current;
        if (h.drawMode) h.onDrawClick([it.lat, it.lng]);
        else if (h.addMode) h.onFeaturePick({ module: moduleKey, item: it });
        else m.openPopup();
      });
      m.on("popupopen", (ev) => {
        const node = ev.popup.getElement && ev.popup.getElement();
        if (!node) return;
        const add = node.querySelector(".fp-add-slack");
        if (add) add.onclick = (e) => { e.stopPropagation(); m.closePopup(); if (handlers.current.onAddSlack) handlers.current.onAddSlack({ module: moduleKey, item: it }); };
        const open = node.querySelector(".fp-open-card");
        if (open) open.onclick = (e) => { e.stopPropagation(); m.closePopup(); handlers.current.onSelect({ module: moduleKey, item: it }); };
      });
      group.addLayer(m);
    });
    map.addLayer(group);
    return () => { map.removeLayer(group); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig, color, moduleKey, singular, display, map, JSON.stringify(iconSpec || null), scale, drawMode]);
  return null;
}

// Domyślne, legalne kafle (OSM z atrybucją). Rzeczywista konfiguracja przekazywana
// jest w propie `tiles` (z Ustawień → Mapa) — pozwala każdej firmie ISP użyć własnego
// dostawcy/licencji. Satelita pojawia się tylko gdy skonfigurowana w ustawieniach.
const DEFAULT_LAYERS = {
  dark: { url: OSM_TILE_URL, cls: "tiles-dark", attr: OSM_ATTR },
  osm: { url: OSM_TILE_URL, cls: "", attr: OSM_ATTR },
};

// Zwraca SVG danego kształtu w kolorze (lub pusty string).
function shapeHtml(id, color) {
  return MARKER_SHAPES[id] ? MARKER_SHAPES[id](color) : "";
}

const LEGEND_OBJ = [["closures", "Mufa"], ["poles", "Słup"], ["olt", "OLT"], ["odf", "Szafa"], ["splitters", "Spliter"], ["onu", "ONU"]];

// Wspólny generator zawartości markera: img > shape > glyph > domyślny. scale skaluje wizualnie.
const safeColor = (c) => (/^[#a-z0-9(),.%\s-]{1,40}$/i.test(String(c || "")) ? String(c) : "#64748b");
function iconInner(color, spec, glyph, scale = 1) {
  color = safeColor(color);
  glyph = escHtml(glyph);
  scale = Number(scale) || 1;
  const tf = `transform:scale(${scale});transform-origin:center center`;
  if (spec && spec.img) {
    return `<div style="width:26px;height:26px;display:flex;align-items:center;justify-content:center;${tf}"><img src="${escHtml(spec.img)}" style="width:100%;height:100%;object-fit:contain" /></div>`;
  }
  if (spec && spec.shape && MARKER_SHAPES[spec.shape]) {
    return `<div style="width:30px;height:30px;display:flex;align-items:center;justify-content:center;filter:drop-shadow(0 1px 2px rgba(0,0,0,.55));${tf}">${MARKER_SHAPES[spec.shape](color)}</div>`;
  }
  const g = escHtml((spec && spec.glyph) || "") || glyph || "";
  if (g && spec && spec.noCircle) {
    return `<div style="width:26px;height:26px;display:flex;align-items:center;justify-content:center;font-size:22px;line-height:1;font-weight:800;color:${color};text-shadow:0 1px 2px rgba(0,0,0,.65),0 0 2px rgba(0,0,0,.5);${tf}">${g}</div>`;
  }
  return `<div class="map-marker" style="background:${color};${tf}">${g}</div>`;
}

function blinkIcon(color, scale = 1) {
  const sz = Math.round(30 * scale);
  return L.divIcon({ className: "", html: `<div class="fp-blink-wrap"><span class="fp-blink-ring"></span><span class="fp-blink-dot" style="background:${safeColor(color)}"></span></div>`, iconSize: [sz, sz], iconAnchor: [sz / 2, sz / 2] });
}

function arrowIcon(angle) {
  return L.divIcon({ className: "", iconSize: [18, 18], iconAnchor: [9, 9],
    html: `<div style="transform:rotate(${angle}deg);color:#EC4899;font-size:15px;line-height:18px;text-align:center;text-shadow:0 0 2px #000;">▲</div>` });
}
function bearingDeg(a, b) {
  const lat = ((a[0] + b[0]) / 2) * Math.PI / 180;
  const dE = (b[1] - a[1]) * Math.cos(lat);
  const dN = (b[0] - a[0]);
  return Math.atan2(dE, dN) * 180 / Math.PI;
}
function markerIcon(color, glyph, spec, scale = 1) {  return L.divIcon({
    className: "",
    html: iconInner(color, spec, glyph, scale),
    iconSize: [26, 26], iconAnchor: [13, 13],
  });
}

// Standard-compliant well (studnia kablowa) marker — square hatch with a manhole cross.
function wellIcon(color, scale = 1) {
  return L.divIcon({
    className: "",
    html: `<div style="width:20px;height:20px;background:${color};border:2px solid #0b1120;box-shadow:0 0 0 1px ${color};display:flex;align-items:center;justify-content:center;border-radius:3px;transform:scale(${scale});transform-origin:center center">
      <span style="font-size:13px;font-weight:800;line-height:1;color:#0b1120">+</span></div>`,
    iconSize: [22, 22], iconAnchor: [11, 11],
  });
}

// Podłączony klient — ikona domku (kolor wg statusu mocy).
function houseIcon(color, scale = 1) {
  return L.divIcon({
    className: "",
    html: `<div style="width:28px;height:28px;display:flex;align-items:center;justify-content:center;filter:drop-shadow(0 1px 2px rgba(0,0,0,.5));transform:scale(${scale});transform-origin:center center">
      <svg width="24" height="24" viewBox="0 0 24 24"><path d="M12 3 L21 10 V21 H14 V15 H10 V21 H3 V10 Z" fill="${color}" stroke="#0b1120" stroke-width="1.3" stroke-linejoin="round"/></svg></div>`,
    iconSize: [28, 28], iconAnchor: [14, 14],
  });
}

// Line style per infrastructure module (kolor + wzór linii wg konwencji teletechnicznej).
const LINE_STYLES = {
  routes: { color: "#06B6D4", weight: 3 },
  cables: { color: "#3B82F6", weight: 4 },
  client_cables: { color: "#F472B6", weight: 3 },
  ducts: { color: "#8B5CF6", weight: 4 },
  microducts: { color: "#22C55E", weight: 3, dashArray: "8 6" },
  pipelines: { color: "#10B981", weight: 3, dashArray: "2 7" },
};

// Pobiera wartość atrybutu z importu (properties) po nazwie kolumny (case-insensitive).
function _impProp(it, names) {
  const p = it.properties || {};
  for (const n of names) {
    for (const k of Object.keys(p)) {
      if (String(k).toLowerCase() === n) return p[k];
    }
    if (it[n] != null && it[n] !== "") return it[n];
  }
  return null;
}

// Distinct marker for pass-through closures (uncut cable running through the mufa).
function passThroughIcon(color, scale = 1) {
  return L.divIcon({
    className: "",
    html: `<div class="map-marker" style="background:${color};box-shadow:0 0 0 2px #0b1120,0 0 0 4px #22d3ee,0 0 6px 4px rgba(34,211,238,0.45);transform:scale(${scale});transform-origin:center center">
      <span style="font-size:12px;font-weight:700;line-height:1;color:#0b1120">⇄</span></div>`,
    iconSize: [26, 26], iconAnchor: [13, 13],
  });
}

// SNMP device status marker (green online / red offline / grey unknown).
function snmpIcon(color) {
  return L.divIcon({    className: "",
    html: `<div class="map-marker" style="background:${color};box-shadow:0 0 0 2px #0b1120,0 0 0 4px ${color}66,0 0 6px 3px ${color}55">
      <span style="font-size:12px;font-weight:800;line-height:1;color:#0b1120">◉</span></div>`,
    iconSize: [26, 26], iconAnchor: [13, 13],
  });
}

// Monter (technician) live-location marker — hard-hat glyph, pulsing ring.
function monterIcon() {
  return L.divIcon({
    className: "",
    html: `<div class="map-marker" style="background:#EAB308;box-shadow:0 0 0 2px #0b1120,0 0 0 4px #eab30866,0 0 8px 4px rgba(234,179,8,0.5)">
      <span style="font-size:13px;line-height:1">👷</span></div>`,
    iconSize: [28, 28], iconAnchor: [14, 14],
  });
}

function haversine(a, b) {
  const R = 6371000, toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b[0] - a[0]), dLng = toRad(b[1] - a[1]);
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a[0])) * Math.cos(toRad(b[0])) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
}

function ClickHandler({ addMode, measureMode, drawMode, pickMode, boxMode, onMapClick, onMeasureClick, onDrawClick, onPickClick, onBoxClick }) {
  useMapEvents({
    click(e) {
      const pt = [e.latlng.lat, e.latlng.lng];
      if (boxMode) onBoxClick(pt);
      else if (pickMode) onPickClick(e.latlng);
      else if (drawMode) onDrawClick(pt);
      else if (addMode) onMapClick(pt);
      else if (measureMode) onMeasureClick(pt);
    },
  });
  return null;
}

function EgibClickHandler({ enabled, onParcelClick }) {
  useMapEvents({
    click(e) { if (enabled) onParcelClick(e.latlng); },
  });
  return null;
}

function RouteEditLayer({ active }) {
  const map = useMap();
  useEffect(() => {
    if (!map.getPane("ap-hit")) {
      map.createPane("ap-hit");
      map.getPane("ap-hit").style.zIndex = 660;
    }
  }, [map]);
  useEffect(() => {
    map.getContainer().classList.toggle("ap-editroute", !!active);
    return () => { try { map.getContainer().classList.remove("ap-editroute"); } catch { /* map unmounted */ } };
  }, [map, active]);
  return null;
}

function ParcelPopup({ info, onClose }) {
  const map = useMap();
  const popupRef = useRef(null);
  const closingRef = useRef(false);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const esc = (v) => String(v ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
    const buildHtml = (i) => {
      if (i.loading) return `<div data-testid="egib-parcel-popup" style="min-width:190px;font-size:12px"><div style="font-weight:600;margin-bottom:3px">Działka ewidencyjna (EGiB)</div><span style="color:#64748b">Pobieranie danych z Geoportalu…</span></div>`;
      if (i.found) return `<div data-testid="egib-parcel-popup" style="min-width:210px;font-size:12px"><div style="font-weight:600;margin-bottom:4px">Działka ewidencyjna (EGiB)</div><div>Nr działki: <b>${esc(i.numer) || "—"}</b></div><div>Obręb: ${esc(i.region) || "—"}</div><div style="font-size:11px;color:#64748b;margin-top:3px;word-break:break-all">ID: ${esc(i.parcel) || "—"}</div></div>`;
      return `<div data-testid="egib-parcel-popup" style="min-width:190px;font-size:12px"><div style="font-weight:600;margin-bottom:3px">Działka ewidencyjna (EGiB)</div><span style="color:#ef4444">${esc(i.message) || "Nie znaleziono działki."}</span></div>`;
    };
    if (!info) {
      if (popupRef.current) { closingRef.current = true; map.closePopup(popupRef.current); popupRef.current = null; }
      return;
    }
    if (!popupRef.current) {
      const p = L.popup({ maxWidth: 300 }).setLatLng([info.lat, info.lng]).setContent(buildHtml(info));
      p.on("remove", () => {
        if (!closingRef.current && onCloseRef.current) onCloseRef.current();
        closingRef.current = false;
        if (popupRef.current === p) popupRef.current = null;
      });
      p.openOn(map);
      popupRef.current = p;
    } else {
      popupRef.current.setLatLng([info.lat, info.lng]).setContent(buildHtml(info));
      if (!map.hasLayer(popupRef.current)) popupRef.current.openOn(map);
    }
  }, [info, map]);

  useEffect(() => () => {
    if (popupRef.current) { closingRef.current = true; map.closePopup(popupRef.current); popupRef.current = null; }
  }, [map]);

  return null;
}

function FlyTo({ target }) {
  const map = useMap();
  useEffect(() => {
    if (target && target.lat != null) map.flyTo([target.lat, target.lng], 16, { duration: 1 });
  }, [target, map]);
  return null;
}

function ZoomButtons() {
  const map = useMap();
  const { t } = useI18n();
  return (
    <div className="absolute bottom-8 right-3 z-[500] flex flex-col backdrop-blur-md bg-slate-900/85 border border-slate-700 rounded-md overflow-hidden">
      <button onClick={() => map.zoomIn()} data-testid="zoom-in" title={t("Przybliż")}
        className="w-9 h-9 flex items-center justify-center text-slate-200 hover:bg-slate-700 border-b border-slate-700"><Plus size={18} /></button>
      <button onClick={() => map.zoomOut()} data-testid="zoom-out" title={t("Oddal")}
        className="w-9 h-9 flex items-center justify-center text-slate-200 hover:bg-slate-700"><Minus size={18} /></button>
    </div>
  );
}

function MoveReporter({ onMoveEnd }) {
  const map = useMap();
  useEffect(() => {
    if (!onMoveEnd) return;
    const report = () => { const c = map.getCenter(); onMoveEnd([c.lat, c.lng]); };
    report();
    map.on("moveend", report);
    return () => map.off("moveend", report);
  }, [map, onMoveEnd]);
  return null;
}

function ViewReporter({ onViewChange }) {
  const map = useMap();
  useEffect(() => {
    if (!onViewChange) return;
    const report = () => {
      const bnd = map.getBounds();
      onViewChange({ zoom: map.getZoom(), bounds: { south: bnd.getSouth(), west: bnd.getWest(), north: bnd.getNorth(), east: bnd.getEast() } });
    };
    report();
    map.on("moveend", report);
    return () => map.off("moveend", report);
  }, [map, onViewChange]);
  return null;
}

function FitBounds({ bounds }) {
  const map = useMap();
  useEffect(() => {
    if (bounds && bounds[0] && bounds[1]) {
      try { map.fitBounds(bounds, { padding: [50, 50], maxZoom: 16 }); } catch { /* noop */ }
    }
  }, [map, bounds]);
  return null;
}

// Zapamietanie ostatniego widoku mapy (srodek + zoom) w localStorage —
// po zalogowaniu mapa otwiera sie tam, gdzie skonczono prace.
function readSavedView() {
  try {
    const v = JSON.parse(localStorage.getItem("fp_map_view") || "null");
    if (v && Number.isFinite(v.lat) && Number.isFinite(v.lng) && Number.isFinite(v.zoom)) return v;
  } catch { /* noop */ }
  return null;
}
function ViewPersist() {
  const map = useMap();
  useEffect(() => {
    const save = () => {
      try {
        const c = map.getCenter();
        localStorage.setItem("fp_map_view", JSON.stringify({ lat: c.lat, lng: c.lng, zoom: map.getZoom() }));
      } catch { /* noop */ }
    };
    map.on("moveend", save);
    return () => map.off("moveend", save);
  }, [map]);
  return null;
}
// Skalowanie ikon wg poziomu zoomu — przy oddalaniu markery robia sie male.
// minF = rozmiar przy dalekim oddaleniu (z<=9), maxF = rozmiar przy zblizeniu (z>=16).
function ZoomScale({ onFactor, minF = 0.12, maxF = 1 }) {
  const map = useMap();
  useEffect(() => {
    const upd = () => {
      const z = map.getZoom();
      const f = z >= 16 ? maxF : z <= 9 ? minF : minF + (z - 9) * ((maxF - minF) / 7);
      onFactor(Math.round(f * 100) / 100);
    };
    upd();
    map.on("zoomend", upd);
    return () => map.off("zoomend", upd);
  }, [map, onFactor, minF, maxF]);
  return null;
}

const pinIcon = L.divIcon({
  className: "",
  html: `<div style="width:32px;height:32px;transform:translateY(-6px)"><svg viewBox="0 0 24 24" width="32" height="32" fill="#06B6D4" stroke="#0F172A" stroke-width="1.5"><path d="M12 2C8 2 5 5 5 9c0 5 7 13 7 13s7-8 7-13c0-4-3-7-7-7z"/><circle cx="12" cy="9" r="2.5" fill="#0F172A"/></svg></div>`,
  iconSize: [32, 32], iconAnchor: [16, 26],
});

// Haversine helpers for live route-segment lengths.
function _segMeters(a, b) {
  const R = 6371000, toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b[0] - a[0]), dLng = toRad(b[1] - a[1]);
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a[0])) * Math.cos(toRad(b[0])) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(s)));
}
function _totalMeters(geom) {
  let t = 0;
  for (let i = 1; i < (geom?.length || 0); i++) t += _segMeters(geom[i - 1], geom[i]);
  return t;
}
function _fmtLen(m) { return m >= 1000 ? `${(m / 1000).toFixed(2)} km` : `${m.toFixed(1)} m`; }

// Uchwyty do edycji trasy linii (kabel/kanalizacja/mikrokanalizacja/trasa).
function editVertexIcon(color) {
  return L.divIcon({ className: "", iconSize: [16, 16], iconAnchor: [8, 8],
    html: `<div style="width:14px;height:14px;border-radius:50%;background:#fff;border:3px solid ${color};box-shadow:0 0 4px rgba(0,0,0,.6);cursor:move"></div>` });
}
function editMidIcon() {
  return L.divIcon({ className: "", iconSize: [12, 12], iconAnchor: [6, 6],
    html: `<div style="width:11px;height:11px;border-radius:50%;background:#22d3ee;opacity:.75;border:2px solid #0b1120;cursor:copy"></div>` });
}
function EditableLine({ geom, setGeom, color, onHistory }) {
  if (!geom || geom.length < 2) return null;
  const moveVertex = (i, ll) => setGeom((g) => g.map((p, idx) => (idx === i ? [ll.lat, ll.lng] : p)));
  const removeVertex = (i) => { if (onHistory) onHistory(); setGeom((g) => (g.length > 2 ? g.filter((_, idx) => idx !== i) : g)); };
  const insertAfter = (i) => { if (onHistory) onHistory(); setGeom((g) => {
    const a = g[i], b = g[i + 1];
    const n = g.slice(); n.splice(i + 1, 0, [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2]); return n;
  }); };
  return (
    <>
      <Polyline positions={geom} pathOptions={{ color, weight: 4, opacity: 0.95, dashArray: "6 6" }} interactive={false} />
      {geom.slice(0, -1).map((p, i) => (
        <Marker key={`mid-${i}`} position={[(geom[i][0] + geom[i + 1][0]) / 2, (geom[i][1] + geom[i + 1][1]) / 2]}
          icon={editMidIcon()} zIndexOffset={1500} eventHandlers={{ click: () => insertAfter(i) }}>
          <Tooltip permanent direction="top" offset={[0, -4]} className="seg-len-tip">{_fmtLen(_segMeters(geom[i], geom[i + 1]))}</Tooltip>
        </Marker>
      ))}
      {geom.map((p, i) => (
        <Marker key={`vtx-${i}`} position={p} draggable icon={editVertexIcon(color)} zIndexOffset={2000}
          eventHandlers={{ dragstart: () => onHistory && onHistory(), drag: (e) => moveVertex(i, e.target.getLatLng()), dragend: (e) => moveVertex(i, e.target.getLatLng()), contextmenu: () => removeVertex(i) }} />
      ))}
    </>
  );
}

export default function MapView({
  data, base, addMode, measureMode, onMapClick, onSelect, tracePath,
  flyTarget, layers, measurePts, onMeasureClick, pendingMarker, visibleModules,
  drawMode, drawPoints, onDrawClick, onMoveEnd, powerStatus, rxLayer, egibLayer, gesutLayers, addresses, onViewChange, onAddressConnect, onAddressCoverage, fitBounds, onFeaturePick, coverageFilter = "all", wifi = null, snmpDevices, onSnmpClick, pickMode, onPickClick, boxMode, onBoxClick, boxPts, boxHighlight = [], monterLocs, monterTrack, onMonterTrack, tiles, cableColors, markerIcons, markerScale, markerSizeType, markerZoomMin = 0.12, markerZoomMax = 1, showLegend, deviceLinks = [],
  blinkSplitterId, spotlight, splitterPower, onAddSlack, slackHints, markerColors, showCableLength, menuVariant = "left",
  editLine, onEditLineSave, onEditLineCancel, autoplan, onAutoplanClosureMove, autoplanEditMufy, onAutoplanClosureRemove, autoplanShowDrops = true, autoplanDropLimit = 80, autoplanBuildings = [], autoplanEditRoute = false, onAutoplanSegmentReroute, autoplanStreet = [], autoplanBusy = false,
}) {
  const layersCfg = tiles || DEFAULT_LAYERS;
  const b = layersCfg[base] || layersCfg.osm;
  const { t } = useI18n();
  const vis = visibleModules || {};
  const isVisible = (k) => vis[k] !== false;
  const zoneColor = (tech) => { const s = (tech || "").toLowerCase(); if (s.includes("ftth") || s.includes("światłow") || s.includes("swiatlow") || s.includes("fiber")) return "#22C55E"; if (s.includes("hfc") || s.includes("docsis") || s.includes("koncentr") || s.includes("coax")) return "#F59E0B"; if (s.includes("radio") || s.includes("lte") || s.includes("5g")) return "#3B82F6"; return "#8B5CF6"; };

  const [zoomFactor, setZoomFactor] = useState(1);
  const [editGeom, setEditGeom] = useState(null);
  const [editPast, setEditPast] = useState([]);
  const [editFuture, setEditFuture] = useState([]);
  const editGeomRef = useRef(null);
  const editPastRef = useRef([]);
  const editFutureRef = useRef([]);
  editGeomRef.current = editGeom;
  editPastRef.current = editPast;
  editFutureRef.current = editFuture;
  useEffect(() => {
    setEditGeom(editLine && editLine.geometry ? editLine.geometry.map((p) => [p[0], p[1]]) : null);
    setEditPast([]); setEditFuture([]);
  }, [editLine]);
  const pushEditHistory = () => { setEditPast([...editPastRef.current, editGeomRef.current]); setEditFuture([]); };
  const undoEdit = () => {
    const past = editPastRef.current;
    if (!past.length) return;
    setEditFuture([editGeomRef.current, ...editFutureRef.current]);
    setEditGeom(past[past.length - 1]);
    setEditPast(past.slice(0, -1));
  };
  const redoEdit = () => {
    const future = editFutureRef.current;
    if (!future.length) return;
    setEditPast([...editPastRef.current, editGeomRef.current]);
    setEditGeom(future[0]);
    setEditFuture(future.slice(1));
  };
  const savedView = useMemo(() => readSavedView(), []);
  const [wellPass, setWellPass] = useState({});
  const [parcel, setParcel] = useState(null);
  const egibClickEnabled = !!egibLayer && !addMode && !measureMode && !drawMode && !pickMode && !boxMode;
  const onParcelClick = (latlng) => {
    setParcel({ lat: latlng.lat, lng: latlng.lng, loading: true });
    api.get("/uke/parcel", { params: { lat: latlng.lat, lng: latlng.lng } })
      .then((r) => setParcel({ lat: latlng.lat, lng: latlng.lng, loading: false, ...r.data }))
      .catch(() => setParcel({ lat: latlng.lat, lng: latlng.lng, loading: false, found: false, message: "Błąd pobierania danych działki." }));
  };
  useEffect(() => { if (!egibLayer) setParcel(null); }, [egibLayer]);
  const fetchPassing = (coll, it) => {
    setWellPass((p) => ({ ...p, [it.id]: { loading: true } }));
    api.get(`/passing/${coll}/${it.id}`)
      .then((r) => setWellPass((p) => ({ ...p, [it.id]: r.data })))
      .catch(() => setWellPass((p) => ({ ...p, [it.id]: { cables: [], lines: [] } })));
  };

  const measureDist = useMemo(() => {
    let d = 0;
    for (let i = 1; i < (measurePts?.length || 0); i++) d += haversine(measurePts[i - 1], measurePts[i]);
    return d;
  }, [measurePts]);

  return (
    <div className={`absolute inset-0 ${b.cls}`} style={{ cursor: addMode || measureMode || drawMode || pickMode || boxMode ? "crosshair" : "" }}>
      {autoplanBusy && autoplanBusy.on && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 z-[1200] pointer-events-none" data-testid="ap-busy-overlay">
          <div className="flex items-center gap-2 bg-slate-900/90 border border-cyan-500/40 text-cyan-100 text-sm rounded-full px-4 py-2 shadow-lg backdrop-blur">
            <span className="w-4 h-4 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
            {autoplanBusy.label || "Pracuję…"}
          </div>
        </div>
      )}
      <MapContainer center={savedView ? [savedView.lat, savedView.lng] : [52.2297, 21.0122]} zoom={savedView ? savedView.zoom : 13} zoomControl={false} className="w-full h-full" style={{ background: "#0b1120" }}>
        <TileLayer url={b.url} attribution={b.attr} subdomains={tileSubdomains(b.url)} maxZoom={19} />
        {egibLayer && (
          <WMSTileLayer url="https://integracja.gugik.gov.pl/cgi-bin/KrajowaIntegracjaEwidencjiGruntow"
            layers="dzialki,numery_dzialek" format="image/png" transparent={true} version="1.3.0"
            opacity={0.75} maxZoom={22} attribution="© GUGiK — EGiB (działki ewidencyjne)" />
        )}
        {gesutLayers && (
          <WMSTileLayer key={gesutLayers} url="https://integracja.gugik.gov.pl/cgi-bin/KrajowaIntegracjaUzbrojeniaTerenu"
            layers={gesutLayers} format="image/png" transparent={true} version="1.3.0"
            opacity={0.8} maxZoom={22} attribution="© GUGiK — GESUT (uzbrojenie terenu)" />
        )}
        <ClickHandler addMode={addMode} measureMode={measureMode} drawMode={drawMode} pickMode={pickMode} boxMode={boxMode} onMapClick={onMapClick} onMeasureClick={onMeasureClick} onDrawClick={onDrawClick} onPickClick={onPickClick} onBoxClick={onBoxClick} />
        <RouteEditLayer active={autoplanEditRoute} />
        <EgibClickHandler enabled={egibClickEnabled} onParcelClick={onParcelClick} />
        {parcel && <ParcelPopup info={parcel} onClose={() => setParcel(null)} />}
        {boxPts && boxPts.length === 2 && (
          <Polygon positions={[[boxPts[0][0], boxPts[0][1]], [boxPts[0][0], boxPts[1][1]], [boxPts[1][0], boxPts[1][1]], [boxPts[1][0], boxPts[0][1]]]}
            pathOptions={{ color: "#ef4444", weight: 2, fillColor: "#ef4444", fillOpacity: 0.12, dashArray: "5 5" }} />
        )}
        {boxHighlight && boxHighlight.map((p, i) => (
          <CircleMarker key={`bh-${i}`} center={p} radius={6}
            pathOptions={{ color: "#ef4444", weight: 1, fillColor: "#ef4444", fillOpacity: 0.85 }} />
        ))}
        <FlyTo target={flyTarget} />
        <ZoomButtons />
        <MoveReporter onMoveEnd={onMoveEnd} />
        <ViewReporter onViewChange={onViewChange} />
        <ViewPersist />
        <ZoomScale onFactor={setZoomFactor} minF={Number(markerZoomMin) || 0.12} maxF={Number(markerZoomMax) || 1} />
        <FitBounds bounds={fitBounds} />

        {/* RX quality layer (toggle) — kolorowe aureole wg jakości sygnału RX klienta */}
        {rxLayer && (data.clients || []).filter((c) => c.lat != null && powerStatus && powerStatus[c.id]).map((c) => {
          const st = powerStatus[c.id];
          const col = st === "krytyczny" ? "#DC2626" : st === "ostrzezenie" ? "#F59E0B" : "#22C55E";
          return (
            <CircleMarker key={`rx-${c.id}`} center={[c.lat, c.lng]} radius={13} interactive={false}
              pathOptions={{ color: col, weight: 2, fillColor: col, fillOpacity: 0.28 }} />
          );
        })}

        {/* Address base points */}
        {(addresses || []).map((a, i) => {
          const connected = !!a.has_client;
          const col = connected ? "#22C55E" : "#F97316";
          return (
            <CircleMarker key={`addr-${i}`} center={[a.lat, a.lng]} radius={4}
              pathOptions={{ color: col, weight: 1, fillColor: col, fillOpacity: 0.9 }}>
              <Popup>
                <b>{a.label || t("Adres")}</b>
                {a.locality ? <><br />{a.locality}</> : null}
                {a.teryt ? <><br />TERYT: {a.teryt}</> : null}
                {a.simc ? <> · SIMC: {a.simc}</> : null}
                <br /><span style={{ color: col, fontWeight: 600 }}>● {connected ? t("Podłączony klient") : t("Brak klienta (potencjał)")}</span>
                {onAddressConnect && !connected && (
                  <><br /><button onClick={() => onAddressConnect(a)} data-testid="address-connect-btn"
                    style={{ marginTop: 6, padding: "4px 8px", background: "#2563EB", color: "#fff", border: 0, borderRadius: 4, cursor: "pointer" }}>
                    {t("Podłącz klienta")}
                  </button></>
                )}
                {onAddressCoverage && (
                  <><br /><button onClick={() => onAddressCoverage(a)} data-testid="address-coverage-btn"
                    style={{ marginTop: 6, padding: "4px 8px", background: "#0d9488", color: "#fff", border: 0, borderRadius: 4, cursor: "pointer" }}>
                    {t("Dodaj zasięg")}
                  </button></>
                )}
              </Popup>
            </CircleMarker>
          );
        })}

        {pendingMarker && (
          <Marker position={pendingMarker} icon={pinIcon} zIndexOffset={1000}>
            <Popup>{t("Nowa lokalizacja (niezapisana)")}</Popup>
          </Marker>
        )}

        {(slackHints || []).map((h, i) => (
          <CircleMarker key={`slackhint-${i}`} center={[h.lat, h.lng]} radius={18}
            pathOptions={{ color: "#F43F5E", weight: 3, fillColor: "#F43F5E", fillOpacity: 0.15, className: "slack-hint-ring" }} interactive={false}>
            <Tooltip permanent direction="top" offset={[0, -8]} className="slack-hint-tip">
              ≈{h.meters} m · {h.reserve_type}{h.host_label ? ` ${h.host_label}` : ""}
            </Tooltip>
          </CircleMarker>
        ))}
        {(slackHints || []).map((h, i) => (
          <CircleMarker key={`slackhint-dot-${i}`} center={[h.lat, h.lng]} radius={4}
            pathOptions={{ color: "#fff", weight: 1.5, fillColor: "#F43F5E", fillOpacity: 1 }} interactive={false} />
        ))}


        {/* Imported GIS layers */}
        {(layers || []).filter((l) => l.visible !== false).map((layer) =>
          (layer.features || []).map((f, i) => {
            const g = f.geometry || {};
            if (g.type === "Point") {
              const [lng, lat] = g.coordinates;
              return <Marker key={`${layer.id}-${i}`} position={[lat, lng]} icon={markerIcon("#64748B")}>
                <Popup>{f.properties?.name || t("Obiekt GIS")}{f.properties?.description ? <><br /><span className="text-slate-400">{f.properties.description}</span></> : null}</Popup></Marker>;
            }
            if (g.type === "LineString") {
              const pts = g.coordinates.map((c) => [c[1], c[0]]);
              return <Polyline key={`${layer.id}-${i}`} positions={pts} pathOptions={{ color: "#64748B", weight: 2, dashArray: "4 4" }} />;
            }
            if (g.type === "Polygon") {
              const rings = (g.coordinates || []).map((r) => r.map((c) => [c[1], c[0]]));
              return <Polygon key={`${layer.id}-${i}`} positions={rings} pathOptions={{ color: "#22C55E", weight: 2, fillColor: "#22C55E", fillOpacity: 0.15 }}>
                <Popup>{f.properties?.name || t("Strefa / zasięg")}{f.properties?.description ? <><br /><span className="text-slate-400">{f.properties.description}</span></> : null}</Popup></Polygon>;
            }
            return null;
          })
        )}

        {/* Strefy / zasięgi (poligony) */}
        {isVisible("zones") && (data.zones || []).filter((z) => (z.geometry || []).length >= 3).map((z) => {
          const zc = zoneColor(z.technology);
          return (
          <Polygon key={z.id} positions={z.geometry} pathOptions={{ color: zc, weight: 2, fillColor: zc, fillOpacity: 0.18 }}>
            <Popup><b>{z.name}</b>{z.technology ? <><br />{z.technology}</> : null}{z.bandwidth ? <> · {z.bandwidth} Mb/s</> : null}</Popup>
          </Polygon>
        ); })}

        {/* Line modules: routes, cables, client cables + underground infrastructure */}
        {["routes", "cables", "client_cables", "ducts", "microducts", "pipelines"].filter(isVisible).map((key) =>
          (data[key] || []).filter((it) => it.geometry?.length > 1).map((it) => {
            let opts = { ...(LINE_STYLES[key] || { color: MODULES[key].marker, weight: 3 }), opacity: 0.85 };
            if (key === "ducts") opts = { ...opts, color: ductColor(cableColors) };
            if (key === "microducts") opts = { ...opts, color: microductColor(cableColors), weight: 4 };
            if (key === "cables") {
              const cc = { ...CABLE_DEF, ...(cableColors || {}) };
              opts = { ...opts, color: cc[it.placement] || cc.napowietrzny || opts.color };
            }
            let occLabel = "";
            if (key === "microducts") {
              const total = Number(it.tube_count) || 0;
              const occ = (data.cables || []).filter((c) => c.microduct_id === it.id && c.microduct_tube != null && c.microduct_tube !== "").length;
              if (total > 0) {
                opts = { ...opts, color: occ >= total ? "#EF4444" : occ > 0 ? "#F59E0B" : "#22C55E" };
                occLabel = ` · ${occ}/${total} ${t("zajętych")}`;
              } else if (occ) { occLabel = ` · ${occ} ${t("kabli")}`; }
            }
            let lenLabel = "";
            {
              const gm = _totalMeters(it.geometry);
              const m = it.optical_length_m != null ? it.optical_length_m : (gm || Number(it.length_m) || 0);
              if (m) lenLabel = ` · ${_fmtLen(m)}`;
            }
            const _spotLine = spotlight && spotlight.module === key && spotlight.id === it.id;
            const _blink = _spotLine && spotlight.blink;
            if (spotlight && spotlight.isolate && !_spotLine) return null;
            const _mid = it.geometry[Math.floor(it.geometry.length / 2)];
            return [
              <Polyline key={it.id} positions={it.geometry} pathOptions={_blink ? { ...opts, weight: (opts.weight || 3) + 4, opacity: 1, className: "fp-cable-blink" } : _spotLine ? { ...opts, weight: (opts.weight || 3) + 4, opacity: 1 } : opts}
                eventHandlers={{ click: () => onSelect({ module: key, item: it }) }}>
                <Tooltip sticky opacity={0.95}>{t(MODULES[key].singular)}: {it.number || it.name}{occLabel}{lenLabel}{(() => {
                  const rel = _impProp(it, ["relacja", "relation"]);
                  const dl = _impProp(it, ["długość", "dlugosc", "length"]);
                  return <>{rel ? <><br />{t("Relacja")}: {String(rel)}</> : null}{dl ? <><br />{t("Długość")}: {String(dl)}</> : null}</>;
                })()}</Tooltip>
                <Popup>
                  {t(MODULES[key].singular)}: {it.number || it.name}{occLabel}{lenLabel}
                  <br /><button data-testid={`open-card-${key}-${it.id}`} onClick={() => onSelect({ module: key, item: it })} style={{ marginTop: 4, color: "#0891b2", textDecoration: "underline", background: "none", border: "none", cursor: "pointer", padding: 0 }}>{t("Otwórz kartę obiektu")}</button>
                </Popup>
              </Polyline>,
              (showCableLength && lenLabel && _mid) ? (
                <Marker key={`${it.id}-len`} position={_mid} interactive={false} zIndexOffset={800}
                  icon={L.divIcon({ className: "", iconSize: [64, 16], iconAnchor: [32, 8],
                    html: `<div style="color:#0f172a;font-size:11px;font-weight:700;white-space:nowrap;text-align:center;text-shadow:0 0 2px #fff,0 0 2px #fff,0 0 3px #fff,0 0 4px #fff">${lenLabel.replace(" · ", "")}</div>` })} />
              ) : null,
            ];
          })
        )}

        {/* Poles: clustered for performance with large datasets */}
        {isVisible("poles") && !(spotlight && spotlight.isolate && spotlight.module !== "poles") && (
          <PointCluster items={data.poles} color={(markerColors && markerColors.poles) || MODULES.poles.marker}
            iconSpec={markerIcons ? markerIcons.poles : null} scale={(markerScale || 1) * ((markerSizeType && markerSizeType.poles) || 1) * zoomFactor}
            moduleKey="poles" singular={MODULES.poles.singular} display={MODULES.poles.display}
            onSelect={onSelect} drawMode={drawMode} onDrawClick={onDrawClick}
            addMode={addMode} onFeaturePick={onFeaturePick} onAddSlack={onAddSlack} />
        )}

        {/* Point modules */}
        {deviceLinks.map((lk, i) => (
          <Polyline key={`dl-${i}`} positions={[lk.a, lk.b]}
            pathOptions={{ color: "#EC4899", weight: 2.5, opacity: 0.9, dashArray: "2 7" }}>
            <Tooltip sticky>{lk.a_label} → {lk.b_label}</Tooltip>
          </Polyline>
        ))}
        {deviceLinks.map((lk, i) => (
          <Marker key={`dla-${i}`} position={[(lk.a[0] + lk.b[0]) / 2, (lk.a[1] + lk.b[1]) / 2]}
            icon={arrowIcon(bearingDeg(lk.a, lk.b))} interactive={false} zIndexOffset={600} />
        ))}
        {Object.keys(MODULES).filter((k) => MODULES[k].geo === "point" && k !== "poles").filter(isVisible).map((key) =>
          (data[key] || []).filter((it) => it.lat != null)
            .filter((it) => key !== "coverage" || coverageFilter === "all" || it.rodzaj_zasiegu === coverageFilter)
            .filter((it) => key !== "clients" || vis[`clients:${clientType(it)}`] !== false)
            .map((it) => {
            let color = key === "clients" ? clientTypeColor(it) : ((markerColors && markerColors[key]) || MODULES[key].marker);
            let glyph = "";
            let statusLabel = "";
            const isPassThrough = key === "closures" && (it.pass_through_cables?.length > 0);
            if (key === "clients" && powerStatus) {
              const st = powerStatus[it.id];
              if (st === "ok") { color = "#22C55E"; statusLabel = "moc OK"; }
              else if (st === "ostrzezenie") { color = "#F59E0B"; glyph = "!"; statusLabel = "ostrzeżenie mocy"; }
              else if (st === "krytyczny") { color = "#DC2626"; glyph = "!"; statusLabel = "przekroczony próg mocy"; }
            }
            if (key === "coverage") {
              const teo = it.rodzaj_zasiegu === "teoretyczny";
              color = teo ? "#F59E0B" : "#22C55E";
              statusLabel = teo ? "zasięg teoretyczny" : "zasięg rzeczywisty";
            }
            if (key === "splitters" && splitterPower && splitterPower[it.id]) {
              const p = splitterPower[it.id];
              const ratio = p.total ? p.free / p.total : 0;
              color = p.free <= 0 ? "#DC2626" : ratio <= 0.25 ? "#F59E0B" : "#22C55E";
            }
            if (key === "switches") {
              const total = (Number(it.rj45_ports) || 0) + (Number(it.sfp_ports) || 0);
              const occ = (it.ports || []).filter((p) => p && p.key).length;
              if (total > 0) {
                const free = total - occ;
                color = free <= 0 ? "#DC2626" : (free / total <= 0.25 ? "#F59E0B" : "#22C55E");
                statusLabel = `porty ${occ}/${total} zajęte`;
              }
            }
            if (key === "olt") {
              const total = (Number(it.pon_ports_total) || 0) + (Number(it.sfp_ports) || 0) + (Number(it.ethernet_ports) || 0);
              if (total > 0) {
                const attach = (it.ports || []).filter((p) => p && p.key);
                const sfpEthOcc = attach.filter((p) => p.key.startsWith("sfp-") || p.key.startsWith("eth-")).length;
                const ponSet = new Set(attach.filter((p) => p.key.startsWith("pon-")).map((p) => p.key.slice(4)));
                (data.clients || []).forEach((c) => { if (c.olt_id === it.id && c.pon_port) ponSet.add(String(c.pon_port)); });
                const ponOcc = Math.max(ponSet.size, Number(it.pon_ports_occupied) || 0);
                const occ = Math.min(total, ponOcc + sfpEthOcc);
                const free = total - occ;
                color = free <= 0 ? "#DC2626" : (free / total <= 0.25 ? "#F59E0B" : "#22C55E");
                statusLabel = `porty ${occ}/${total} zajęte`;
              }
            }
            if (it.color) color = safeColor(it.color);
            const spec = markerIcons ? markerIcons[key] : null;
            const hasCustom = spec && (spec.img || spec.glyph);
            const s = (markerScale || 1) * ((markerSizeType && markerSizeType[key]) || 1) * zoomFactor;
            const isSpot = spotlight && spotlight.module === key && spotlight.id === it.id;
            if (spotlight && spotlight.isolate && !isSpot) return null;
            const isBlink = isSpot || (key === "splitters" && blinkSplitterId && it.id === blinkSplitterId);
            const icon = isBlink ? blinkIcon(color, Math.max(s, 1.4))
              : (key === "wells" ? wellIcon(color, s)
              : (key === "clients" && !hasCustom ? houseIcon(color, s)
              : (isPassThrough && !hasCustom ? passThroughIcon(color, s) : markerIcon(color, glyph, spec, s))));
            const powerOn = !!(key === "splitters" && splitterPower && splitterPower[it.id]);
            return (
              <Marker key={`${it.id}-${powerOn ? "pw" : "np"}-${zoomFactor}`} position={[it.lat, it.lng]} icon={icon} zIndexOffset={isSpot ? 3000 : (isBlink ? 2000 : 0)}
                eventHandlers={{ click: () => { if (drawMode) onDrawClick([it.lat, it.lng]); else if (addMode) onFeaturePick({ module: key, item: it }); else if (key === "wells" || key === "closures") fetchPassing(key, it); else onSelect({ module: key, item: it }); } }}>
                <Popup>
                  {spec?.img ? <img src={spec.img} alt="" style={{ width: 16, height: 16, objectFit: "contain", verticalAlign: "middle", marginRight: 4 }} />
                    : spec?.shape ? <span style={{ display: "inline-block", width: 16, height: 16, verticalAlign: "middle", marginRight: 4 }} dangerouslySetInnerHTML={{ __html: shapeHtml(spec.shape, color) }} />
                    : (spec?.glyph ? <span style={{ marginRight: 4 }}>{spec.glyph}</span> : (key === "clients" ? <span style={{ marginRight: 4 }}>⌂</span> : null))}
                  {(() => { const Ico = MODULES[key].icon; return Ico ? <Ico size={14} color={color} style={{ verticalAlign: "middle", marginRight: 5, display: "inline" }} /> : null; })()}
                  <b>{t(MODULES[key].singular)}</b>: {it[MODULES[key].display] || it.name}
                  {isPassThrough && <><br /><span style={{ color: "#0891b2" }}>⇄ Mufa przelotowa · {it.pass_through_cables.length} kabel(i) przelotowych</span></>}
                  {statusLabel && <><br /><span style={{ color }}>● {statusLabel}</span></>}
                  {(key === "wells" || key === "closures") && (() => {
                    const wp = wellPass[it.id];
                    if (!wp || wp.loading) return <><br /><span style={{ color: "#64748b" }}>{t("Ładowanie…")}</span></>;
                    const lines = wp.lines || []; const cables = wp.cables || [];
                    return <>
                      {(!lines.length && !cables.length)
                        ? <><br /><span style={{ color: "#64748b" }}>{t("Brak kabli/tras w pobliżu")}</span></>
                        : <><br /><b>{t("Przebiega:")}</b>
                            {lines.map((l) => <span key={l.module + l.id}><br />• {t(MODULES[l.module]?.singular || l.module)}: {l.name}{l.tube_count ? ` (${l.tube_count} ${t("mikrorur")})` : ""}</span>)}
                            {cables.map((c) => <span key={c.id}><br />• {t("Kabel")} {c.number} — {c.free}/{c.fiber_count} {t("wolnych")}{c.microduct_tube ? ` · ${t("mikrorura")} ${c.microduct_tube}` : ""}</span>)}
                          </>}
                      {onAddSlack && <><br /><button data-testid={`add-slack-${key}-${it.id}`} onClick={() => onAddSlack({ module: key, item: it })} style={{ marginTop: 6, color: "#fff", background: "#F43F5E", border: "none", borderRadius: 4, padding: "4px 8px", cursor: "pointer" }}>+ {t("Dodaj zapas kabla")}</button></>}
                      <br /><button data-testid={`open-${key}-${it.id}`} onClick={() => onSelect({ module: key, item: it })} style={{ marginTop: 6, color: "#fff", background: "#0891b2", border: "none", borderRadius: 4, padding: "4px 8px", cursor: "pointer" }}>{t("Wyświetl kartę obiektu")}</button>
                    </>;
                  })()}
                  {key !== "wells" && key !== "closures" && (
                    <>
                      {onAddSlack && (key === "cabinets" || key === "onu") && <><br /><button data-testid={`add-slack-${key}-${it.id}`} onClick={() => onAddSlack({ module: key, item: it })} style={{ marginTop: 6, color: "#fff", background: "#F43F5E", border: "none", borderRadius: 4, padding: "4px 8px", cursor: "pointer" }}>+ {t("Dodaj zapas kabla")}</button></>}
                      <br /><button data-testid={`open-card-${key}-${it.id}`} onClick={() => onSelect({ module: key, item: it })} style={{ marginTop: 6, color: "#fff", background: "#0891b2", border: "none", borderRadius: 4, padding: "4px 8px", cursor: "pointer" }}>{t("Wyświetl kartę obiektu")}</button>
                    </>
                  )}
                </Popup>
                {!drawMode && (
                  <Tooltip direction="top" offset={[0, -12]} opacity={0.95} permanent={powerOn}>
                    {t(MODULES[key].singular)}: {it[MODULES[key].display] || it.name || "—"}{statusLabel ? ` · ${statusLabel}` : ""}
                    {key === "splitters" && splitterPower && splitterPower[it.id] ? ` · ⚡${splitterPower[it.id].dbm} dBm · ${splitterPower[it.id].occupied}/${splitterPower[it.id].total} zajęte, ${splitterPower[it.id].free} wolne` : ""}
                  </Tooltip>
                )}
              </Marker>
            );
          })
        )}

        {/* SNMP device status layer (green=online, red=offline, grey=unknown) */}
        {!(spotlight && spotlight.isolate) && (snmpDevices || []).filter((d) => d.lat != null && d.lng != null).map((d) => {
          const col = d.last_status === "online" ? "#22C55E" : d.last_status === "offline" ? "#DC2626" : "#64748B";
          return (
            <Marker key={`snmp-${d.id}`} position={[d.lat, d.lng]} icon={snmpIcon(col)} zIndexOffset={900}>
              <Popup>
                <b>SNMP · {d.name}</b><br />{d.host}:{d.snmp_port}
                <br /><span style={{ color: col, fontWeight: 600 }}>● {t(d.last_status || "unknown")}</span>
                {d.last_ts ? <><br /><span style={{ color: "#64748b" }}>{t("Ostatnie odpytanie")}: {String(d.last_ts).slice(0, 19).replace("T", " ")}</span></> : null}
                {d.maintenance ? <><br /><span style={{ color: "#F59E0B" }}>⚙ {t("Tryb serwisowy (ignoruj alarmy)")}</span></> : null}
                {onSnmpClick && (
                  <><br /><button onClick={() => onSnmpClick(d.id)} data-testid="snmp-popup-live"
                    style={{ marginTop: 6, padding: "4px 8px", background: "#0891b2", color: "#fff", border: 0, borderRadius: 4, cursor: "pointer" }}>
                    {t("Podgląd na żywo")}
                  </button></>
                )}
              </Popup>
            </Marker>
          );
        })}

        {/* Live monter locations layer */}
        {!(spotlight && spotlight.isolate) && (monterLocs || []).filter((m) => m.lat != null && m.lng != null).map((m) => {
          const mins = m.ts ? Math.round((Date.now() - new Date(m.ts).getTime()) / 60000) : null;
          const ago = mins == null ? "" : mins < 1 ? t("przed chwilą") : `${mins} ${t("min temu")}`;
          return (
            <Marker key={`monter-${m.user_id}`} position={[m.lat, m.lng]} icon={monterIcon()} zIndexOffset={1100}>
              <Popup>
                <b>👷 {m.name}</b>
                {m.active_task ? <><br />{t("Zlecenie")}: {m.active_task.task_no} · {m.active_task.title}</> : <><br /><span style={{ color: "#64748b" }}>{t("Brak aktywnego zlecenia")}</span></>}
                {ago ? <><br /><span style={{ color: "#64748b" }}>{t("Zaktualizowano")}: {ago}</span></> : null}
                {onMonterTrack && (
                  <><br /><button onClick={() => onMonterTrack(m.user_id)} data-testid="monter-track-btn"
                    style={{ marginTop: 6, padding: "4px 8px", background: "#EAB308", color: "#0b1120", border: 0, borderRadius: 4, cursor: "pointer", fontWeight: 600 }}>
                    {t("Ślad trasy (6h)")}
                  </button></>
                )}
              </Popup>
            </Marker>
          );
        })}

        {/* Monter track (route history) */}
        {(monterTrack || []).length > 1 && (
          <Polyline positions={monterTrack} pathOptions={{ color: "#EAB308", weight: 3, opacity: 0.85, dashArray: "6 6" }} />
        )}

        {/* Signal trace highlight */}
        {tracePath?.length > 1 && (
          <>
            <Polyline positions={tracePath} pathOptions={{ color: "#06B6D4", weight: 7, opacity: 0.35 }} />
            <Polyline positions={tracePath} className="trace-glow"
              pathOptions={{ color: "#22D3EE", weight: 3, dashArray: "12 12" }} />
          </>
        )}

        {/* Measurement */}
        {measurePts?.length > 1 && (
          <Polyline positions={measurePts} pathOptions={{ color: "#EAB308", weight: 2, dashArray: "6 6" }} />
        )}
        {measurePts?.map((p, i) => (
          <Marker key={`m${i}`} position={p} icon={markerIcon("#EAB308")} />
        ))}

        {/* Route drawing in progress */}
        {drawPoints?.length > 1 && (
          <Polyline positions={drawPoints} pathOptions={{ color: "#22D3EE", weight: 3, dashArray: "8 8" }} />
        )}
        {drawPoints?.map((p, i) => (
          <Marker key={`d${i}`} position={p} icon={markerIcon(p.snapped ? "#22C55E" : "#22D3EE", "")} />
        ))}

        {/* Edycja trasy istniejącej linii */}
        {editLine && editGeom && <EditableLine geom={editGeom} setGeom={setEditGeom} color="#F59E0B" onHistory={pushEditHistory} />}

        {/* Podgląd Auto-projektu (szkic) — kolorowa warstwa przed zapisem */}
        {(autoplanBuildings || []).map((ring, i) => (
          <Polygon key={`ap-bld-${i}`} positions={ring} pathOptions={{ color: "#64748B", weight: 1, fillColor: "#94A3B8", fillOpacity: 0.35 }} interactive={false} />
        ))}
        {autoplan && autoplanShowDrops && (autoplan.drops || []).map((dr, i) => {
          const over = autoplanDropLimit > 0 && dr.length_m > autoplanDropLimit;
          return (
            <Polyline key={`ap-drop-${i}`} positions={dr.geometry} pathOptions={{ color: over ? "#EF4444" : "#FBBF24", weight: over ? 2.5 : 1.5, opacity: over ? 0.85 : 0.6 }}>
              <Tooltip sticky>{dr.label || "Przyłącze"} → {dr.mufa} · {Math.round(dr.length_m)} m{over ? " · ⚠ przekroczony limit" : ""}</Tooltip>
            </Polyline>
          );
        })}
        {autoplanStreet && autoplanStreet.length > 1 && (
          <Polyline positions={autoplanStreet} pathOptions={{ color: "#F97316", weight: 5, opacity: 0.7, dashArray: "10 6" }} interactive={false} />
        )}
        {autoplanStreet && autoplanStreet.map((p, i) => (
          <CircleMarker key={`ap-street-${i}`} center={p} radius={4} pathOptions={{ color: "#F97316", fillColor: "#F97316", fillOpacity: 0.9 }} interactive={false} />
        ))}
        {autoplan && autoplan.segments && autoplan.segments.map((s, i) => {
          const pos = (s.geometry && s.geometry.length > 1) ? s.geometry : [[s.from.lat, s.from.lng], [s.to.lat, s.to.lng]];
          const color = (s.route_type || "").startsWith("słup") ? "#22D3EE" : "#F59E0B";
          if (autoplanEditRoute) {
            return [
              <Polyline key={`ap-seg-v-${i}`} positions={pos} pathOptions={{ color: "#A3E635", weight: 5, opacity: 0.95 }} interactive={false} />,
              <Polyline key={`ap-seg-h-${i}`} positions={pos} pane="ap-hit" pathOptions={{ color: "#A3E635", weight: 22, opacity: 0 }} interactive eventHandlers={{ click: (e) => onAutoplanSegmentReroute && onAutoplanSegmentReroute(i, e.latlng) }}>
                <Tooltip sticky>Kliknij, aby poprowadzić trasę przez ten punkt (po słupach)</Tooltip>
              </Polyline>,
            ];
          }
          return (
            <Polyline key={`ap-seg-${i}`} positions={pos} pathOptions={{ color, weight: 4, opacity: 0.9, dashArray: "3 7" }} interactive>
              <Tooltip sticky>{`${s.from.label} → ${s.to.label} · ${Math.round(s.length_m)} m · ${s.fiber_count}J · ${s.route_type}`}</Tooltip>
            </Polyline>
          );
        })}
        {autoplan && autoplan.nodes && autoplan.nodes.map((n, i) => (
          n.kind === "cabinet" ? (
            <Marker key={`ap-node-${i}`} position={[n.lat, n.lng]} icon={markerIcon("#A855F7", "")} zIndexOffset={1600} interactive={!autoplanEditRoute}>
              <Tooltip permanent direction="top" offset={[0, -8]}>{t("Szafa")}</Tooltip>
            </Marker>
          ) : (
            <Marker key={`ap-node-${i}`} position={[n.lat, n.lng]} draggable={!autoplanEditMufy && !autoplanEditRoute && !!onAutoplanClosureMove} interactive={!autoplanEditRoute} icon={markerIcon(autoplanEditMufy ? "#F43F5E" : (n.closure_kind === "kopułkowa" ? "#F97316" : "#06B6D4"), "")} zIndexOffset={1600}
              eventHandlers={{
                dragend: (e) => onAutoplanClosureMove && onAutoplanClosureMove(i, e.target.getLatLng()),
                click: () => { if (autoplanEditMufy && onAutoplanClosureRemove) onAutoplanClosureRemove(i); },
              }}>
              <Tooltip permanent direction="top" offset={[0, -8]}>{n.label}{n.closure_kind ? ` (${n.closure_kind})` : ""} · {n.clients || 0} {t("bud.")}{autoplanEditMufy ? " · klik=usuń" : ""}</Tooltip>
            </Marker>
          )
        ))}
        {wifi && <WifiMapLayer wifi={wifi} />}
      </MapContainer>

      {showLegend !== false && cableColors && (
        <details open data-testid="map-legend"
          className={`absolute bottom-6 z-[600] kv-glass rounded-xl px-3 py-2 text-[11px] text-slate-200 max-w-[190px] ${menuVariant === "left" ? "left-4 md:left-56" : menuVariant === "right" ? "left-4" : "left-40"}`}>
          <summary className="font-semibold text-slate-200 cursor-pointer select-none">{t("Legenda")}</summary>
          <div className="text-slate-400 text-[10px] uppercase mt-1.5 mb-0.5">{t("Kable")}</div>
          {[["napowietrzny", "Napowietrzny"], ["kanalizacja", "Kanalizacja"], ["kanalizacja OPL", "Kanalizacja OPL"], ["kanalizacja własna", "Kanalizacja własna"], ["mikrokanalizacja", "Mikrokanalizacja"], ["doziemny", "Doziemny"]].map(([k, l]) => (
            <div key={k} className="flex items-center gap-2 py-0.5">
              <span style={{ background: { ...CABLE_DEF, ...cableColors }[k] || "#3B82F6" }} className="inline-block w-5 h-1.5 rounded-full" />
              <span>{t(l)}</span>
            </div>
          ))}
          <div className="text-slate-400 text-[10px] uppercase mt-2 mb-0.5">{t("Obiekty")}</div>
          {LEGEND_OBJ.map(([k, l]) => {
            const color = (markerColors && markerColors[k]) || (MODULES[k] && MODULES[k].marker) || "#22d3ee";
            const spec = markerIcons ? markerIcons[k] : null;
            const html = spec && spec.img ? `<img src="${spec.img}" style="width:16px;height:16px;object-fit:contain" />`
              : (spec && spec.shape && MARKER_SHAPES[spec.shape] ? MARKER_SHAPES[spec.shape](color)
                : (spec && spec.glyph && spec.noCircle ? `<span style="display:inline-flex;align-items:center;justify-content:center;font-size:14px;font-weight:800;color:${color}">${spec.glyph}</span>`
                  : (k === "clients" ? MARKER_SHAPES.house(color)
                    : `<span style="display:inline-flex;width:14px;height:14px;border-radius:50%;background:${color};align-items:center;justify-content:center;font-size:9px;color:#0b1120">${(spec && spec.glyph) || ""}</span>`)));
            return (
              <div key={k} className="flex items-center gap-2 py-0.5">
                <span style={{ width: 16, height: 16, display: "inline-flex", alignItems: "center", justifyContent: "center" }} dangerouslySetInnerHTML={{ __html: html }} />
                <span>{t(l)}</span>
              </div>
            );
          })}
          {CLIENT_TYPES.map((ct) => (
            <div key={ct.id} className="flex items-center gap-2 py-0.5" data-testid={`legend-client-${ct.id}`}>
              <span style={{ width: 16, height: 16, display: "inline-flex", alignItems: "center", justifyContent: "center" }} dangerouslySetInnerHTML={{ __html: MARKER_SHAPES.house(ct.color) }} />
              <span>{t(ct.label)}</span>
            </div>
          ))}
          {(data.zones || []).length > 0 && (
            <>
              <div className="text-slate-400 text-[10px] uppercase mt-2 mb-0.5">{t("Strefy")}</div>
              {[["FTTH", "#22C55E"], ["HFC / DOCSIS", "#F59E0B"], ["Radiowa / LTE", "#3B82F6"], ["Inne", "#8B5CF6"]].map(([l, c]) => (
                <div key={l} className="flex items-center gap-2 py-0.5">
                  <span style={{ background: c }} className="inline-block w-4 h-3 rounded-sm border border-slate-600" />
                  <span>{t(l)}</span>
                </div>
              ))}
            </>
          )}
        </details>
      )}

      {measureMode && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-[500] backdrop-blur-md bg-slate-900/90 border border-slate-700 rounded-md px-4 py-2 text-sm">
          Pomiar: <b className="font-mono text-yellow-400">{(measureDist / 1000).toFixed(3)} km</b> ({measureDist.toFixed(0)} m) · kliknij, aby dodać punkty
        </div>
      )}

      {editLine && editGeom && (
        <div className="absolute bottom-28 left-1/2 -translate-x-1/2 z-[950] backdrop-blur-md bg-slate-900/95 border border-amber-500/60 rounded-lg px-4 py-2.5 flex items-center gap-3 shadow-xl" data-testid="editline-bar">
          <div className="text-xs text-slate-300 max-w-[320px]">
            <b className="text-amber-400">{t("Edycja trasy")}</b> · {editGeom.length} {t("pkt")} · <b className="text-cyan-300" data-testid="editline-total">{_fmtLen(_totalMeters(editGeom))}</b>
            <div className="text-[10px] text-slate-500">{t("Przeciągnij punkt · klik na środku odcinka = dodaj · prawy klik = usuń")}</div>
          </div>
          <button data-testid="editline-undo" disabled={!editPast.length} onClick={undoEdit} title={t("Cofnij")}
            className="bg-slate-700 hover:bg-slate-600 disabled:opacity-40 text-white rounded px-2.5 py-1.5 text-sm font-mono">↶</button>
          <button data-testid="editline-redo" disabled={!editFuture.length} onClick={redoEdit} title={t("Ponów")}
            className="bg-slate-700 hover:bg-slate-600 disabled:opacity-40 text-white rounded px-2.5 py-1.5 text-sm font-mono">↷</button>
          <button data-testid="editline-save" onClick={() => onEditLineSave && onEditLineSave(editLine, editGeom)}
            className="bg-green-600 hover:bg-green-500 text-white rounded px-3 py-1.5 text-sm font-medium">{t("Zapisz trasę")}</button>
          <button data-testid="editline-cancel" onClick={() => onEditLineCancel && onEditLineCancel()}
            className="bg-slate-700 hover:bg-slate-600 text-white rounded px-3 py-1.5 text-sm">{t("Anuluj")}</button>
        </div>
      )}
    </div>
  );
}
